import React from 'react';
import PropTypes from 'prop-types';

import LoaderProgress from './LoaderProgress/LoaderProgress';
import ErrorDialog from '../ErrorDialog/ErrorDialog';

import constants from '../../constants.js';
import { getLoadedStatus } from '../../helpers/loaderHelpers.js';

import styles from './styles';
import { withStyles } from '@material-ui/core/styles';

function withLoader(WrappedComponent) {
  function WithLoader(props) {
    const {
      classes,
      loaded,
      hideWhileLoading,
      onRefresh,
      onError,
      loadText,
      failureText,
      notFoundText,
      inOutage,
      outageFrom,
      outageTo,
      outageMessage,
      forceOutage,
      onLoadComplete,
      ...rest
    } = props;

    const loadedStatus = getLoadedStatus(loaded);
    const unloaded =
      loadedStatus == constants.UNLOADED ||
      loadedStatus == constants.FAILED ||
      loadedStatus == constants.FAILED_FATAL ||
      loadedStatus == constants.FAILED_NOT_FOUND;
    const isLoading = loadedStatus == constants.UNLOADED;
    const loadFailed =
      loadedStatus == constants.FAILED ||
      loadedStatus == constants.FAILED_FATAL;

    const loading = <LoaderProgress show={isLoading} />;

    const failedFatal = (
      <div className={classes.loader}>
        <ErrorDialog logout={onError} text={failureText} />
      </div>
    );

    const failed = (
      <div className={classes.loader}>
        <ErrorDialog logout={onError} text={failureText} />
      </div>
    );

    const failedNotFound = (
      <div className={classes.loader}>
        <ErrorDialog logout={onError} text={notFoundText} type='notfound' />
      </div>
    );

    const failedOutage = <ErrorDialog text={outageMessage} logout={onError} />;

    // If there is a onLoadComplete function call that if loading is complete.
    !isLoading && onLoadComplete && onLoadComplete();

    return (
      <div className={unloaded ? classes.parent : classes.maxWidth}>
        <div
          className={
            (hideWhileLoading || loadFailed) && unloaded
              ? classes.shown
              : classes.hidden
          }
        >
          {loadedStatus == constants.UNLOADED && loading}
          {!inOutage && loadedStatus == constants.FAILED && failed}
          {!inOutage && loadedStatus == constants.FAILED_FATAL && failedFatal}
          {loadedStatus == constants.FAILED_NOT_FOUND && failedNotFound}
          {inOutage && (unloaded || forceOutage) && failedOutage}
        </div>
        <div
          className={
            ((hideWhileLoading || loadFailed) && unloaded) ||
            (inOutage && (unloaded || forceOutage))
              ? classes.hidden
              : ''
          }
        >
          <WrappedComponent {...rest} />
        </div>
      </div>
    );
  }

  const wrappedComponentName =
    WrappedComponent.displayName || WrappedComponent.name || 'Component';

  WithLoader.displayName = `withLoader(${wrappedComponentName})`;
  WithLoader.propTypes = {
    classes: PropTypes.object.isRequired,
    loaded: PropTypes.array.isRequired,
    hideWhileLoading: PropTypes.bool,
    onRefresh: PropTypes.func,
    onError: PropTypes.func,
    loadText: PropTypes.string,
    failureText: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    notFoundText: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    inOutage: PropTypes.bool,
    outageFrom: PropTypes.string,
    outageTo: PropTypes.string,
    forceOutage: PropTypes.bool,
    onLoadComplete: PropTypes.func,
  };

  WithLoader.defaultProps = {
    hideWhileLoading: true,
    onRefresh: null,
    onError: null,
    loadText: 'Loading',
    failureText: '',
    notFoundText: '',
    inOutage: false,
    outageFrom: '',
    outageTo: '',
    outageMessage: '',
    forceOutage: false,
    onLoadComplete: null,
  };

  return withStyles(styles, { withTheme: true })(WithLoader);
}

export default withLoader;
